import { isEqual } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import {
  CommanderMonitoringFilterLogicalObject,
  getFilterLogicalObjectFromZipDeviceControlUnits,
} from '../api/util/filter/filter.model';
import { filterAnalogChannels } from '../api/util/filter/filterAnalogChannels';
import { filterDigitalChannels } from '../api/util/filter/filterDigitalChannels';
import { STTimeLineChartData, TimeFrame } from '../api/monitoring-ui.model';
import { checkDefaultChannels } from '../api/util/filter/checkDefaultChannels';
import { getLevelOfView } from '../../groups/selectors/getLevelOfView';
import { useAppDispatch, useAppSelector } from '../../store.model';
import { getFilterStored } from '../selectors';
import { useDevicesControlUnits } from '../../controlUnit/hooks/useDevicesControlUnits';
import { updateFilter } from '../thunk';
import { filterExtrapolatedDigitalChannelData } from '../api/util/filter/filterExtrapolatedDigitalChannelData';
import { useCUsTranslate } from '../../CUTranslation/hooks/useCUsTranslate';
import { TIME_FRAME_TYPES, TIME_RANGE_TYPES } from 'stoerk-ui-components';
import usePreferredTemperatureUnit from '../../settings/hooks/usePreferredTemperatureUnit';
import { areFiltersUpdated } from '../../groups/selectors/areFiltersUpdated';
import useLabStateFilters from '../../../components/DeviceManager/Device/Monitoring/components/Lab/useLabStateFilters';
import { useAdaptableParams } from '../../../util/hooks/useAdaptableParams';
import MonitoringAPI from '../api/MonitoringAPI';
import { getSensorInformation } from './useSelectors';
import useMultipleQueries from '../../../util/rtk/useQueries';

export interface UseMetricsProps {
  initialTimeFrame?: TimeFrame;
  showAllMetrics?: boolean; // If true, it will not fetch data again if already fetched
  defaultAnalogFilter?: CommanderMonitoringFilterLogicalObject;
  defaultDigitalFilter?: CommanderMonitoringFilterLogicalObject;
}

export const useMetrics = (props: UseMetricsProps = {}) => {
  const initialMemoizedTimeFrame = useMemo(() => {
    return {
      start: moment().subtract(6, 'hours'),
      end: moment(),
      timeFrameType: TIME_FRAME_TYPES.LAST_6_HOURS,
      timeRangeType: TIME_RANGE_TYPES.QUICK,
    };
  }, []);
  const {
    initialTimeFrame = initialMemoizedTimeFrame,
    showAllMetrics = false,
    defaultAnalogFilter,
    defaultDigitalFilter,
  } = props;
  const params = useAdaptableParams<{
    deviceId?: string;
    groupId?: string;
    controlUnitId?: string;
  }>();
  const showLevelView = getLevelOfView(params);
  const dispatch = useAppDispatch();

  const [devicesControlUnits, loadingDevicesControlUnits] =
    useDevicesControlUnits();
  const [, loadingCUsTranslate] = useCUsTranslate();
  const [readyToFetch, setReadyToFetch] = useState(false);

  // Filter controls
  const [timeFrame, setTimeFrame] = useState<TimeFrame>(initialTimeFrame);

  const defaultFilterObjectEventsStored = useAppSelector(
    (store) => getFilterStored(store, { ...params, type: 'events' }),
    isEqual
  );
  const defaultFilterObjectChannelsStored = useAppSelector(
    (store) => getFilterStored(store, { ...params, type: 'channels' }),
    isEqual
  );
  const defaultFilterObjectChannels = useMemo(() => {
    if (defaultAnalogFilter) return defaultAnalogFilter;
    // Hydrate stored value if not exists or has all the CUs
    return !showAllMetrics &&
      defaultFilterObjectChannelsStored &&
      areFiltersUpdated(
        devicesControlUnits,
        defaultFilterObjectChannelsStored,
        'channels'
      )
      ? defaultFilterObjectChannelsStored
      : // Get default
        checkDefaultChannels(
          getFilterLogicalObjectFromZipDeviceControlUnits(
            devicesControlUnits,
            'channels',
            false
          ),
          showAllMetrics
        );
  }, [devicesControlUnits]);

  const defaultFilterObjectEvents = useMemo(() => {
    if (defaultDigitalFilter) return defaultDigitalFilter;
    // Hydrate stored value if not exists or has all the CUs
    return !showAllMetrics &&
      defaultFilterObjectEventsStored &&
      areFiltersUpdated(
        devicesControlUnits,
        defaultFilterObjectEventsStored,
        'events'
      )
      ? defaultFilterObjectEventsStored
      : // Get default
        checkDefaultChannels(
          getFilterLogicalObjectFromZipDeviceControlUnits(
            devicesControlUnits,
            'events',
            false
          ),
          showAllMetrics
        );
  }, [devicesControlUnits]);

  const devicesIds = useMemo(() => {
    return Array.from(
      new Set<string>(devicesControlUnits.map((d) => d.deviceId))
    );
  }, [devicesControlUnits]);

  const { data: preferredTemperatureUnit } = usePreferredTemperatureUnit();
  const digitalControls = useState<CommanderMonitoringFilterLogicalObject>(
    defaultFilterObjectEvents
  );
  const analogControls = useState<CommanderMonitoringFilterLogicalObject>(
    defaultFilterObjectChannels
  );
  const [digitalFilterObjectEvents, setDigitalFilterObject] = digitalControls;
  const [analogFilterObjectEvents, setAnalogFilterObject] = analogControls;
  const externalSetDigitalFilterObject: typeof setDigitalFilterObject = (
    value
  ) => {
    setDigitalFilterObject(value);
    dispatch(
      updateFilter({
        ...params,
        type: 'events',
        value: value as CommanderMonitoringFilterLogicalObject,
      })
    );
  };
  const externalSetAnalogFilterObject: typeof setAnalogFilterObject = (
    value
  ) => {
    setAnalogFilterObject(value);
    dispatch(
      updateFilter({
        ...params,
        type: 'channels',
        value: value as CommanderMonitoringFilterLogicalObject,
      })
    );
  };
  const labStateFilters = useLabStateFilters(devicesControlUnits);
  const [connectionDigitalFilterObject] =
    labStateFilters.connectionDigitalFilterControls;
  const [cloudGeneratedFilterLogicalObject] =
    labStateFilters.cloudGeneratedFilterControls;

  const labBandwidthFeatureArgs = useMemo(() => {
    return devicesIds.map((deviceId) => ({
      deviceId,
      start: timeFrame.start.valueOf(),
      end: timeFrame.end.valueOf(),
      commanderFilterObject: cloudGeneratedFilterLogicalObject[deviceId] || {},
      includeCloudGenerated: true,
    }));
  }, [
    cloudGeneratedFilterLogicalObject,
    timeFrame.start,
    timeFrame.end,
    devicesIds,
    labStateFilters.labFiltersFeatures.labBandwidthFeature,
    labStateFilters.connectionDigitalFilterControls[0],
  ]);
  const connectionArgs = useMemo(() => {
    return devicesIds
      .map((deviceId) => {
        const channels = Object.values(
          connectionDigitalFilterObject[deviceId] || {}
        ).flatMap((controlUnitObject = {}) =>
          Object.entries(controlUnitObject)
            .filter(([_key, value]) => !!value)
            .map(([key, _value]) => key)
        );
        return {
          channels,
          deviceId,
          start: moment(timeFrame.start).add(-1, 'day').valueOf(),
          end: moment(timeFrame.end).add(1, 'day').valueOf(),
        };
      })
      .filter((arg) => !!arg.channels.length);
  }, [
    devicesIds,
    connectionDigitalFilterObject,
    timeFrame.start,
    timeFrame.end,
    labStateFilters.labFiltersFeatures.labConnectionFeature,
    labStateFilters.cloudGeneratedFilterControls[0],
  ]);

  const eventsArgs = useMemo(() => {
    return devicesIds.map((deviceId) => ({
      deviceId,
      start: moment(timeFrame.start).add(-1, 'day').valueOf(),
      end: moment(timeFrame.end).add(1, 'day').valueOf(),
      commanderFilterObject: digitalFilterObjectEvents[deviceId] || {},
    }));
  }, [devicesIds, digitalFilterObjectEvents, timeFrame.start, timeFrame.end]);

  const sensorArgs = useMemo(
    () =>
      devicesIds.map((deviceId) => ({
        deviceId,
        start: timeFrame.start.valueOf(),
        end: timeFrame.end.valueOf(),
        commanderFilterObject: analogFilterObjectEvents[deviceId] || {},
        includeCloudGenerated: false,
      })),
    [devicesIds, analogFilterObjectEvents, timeFrame.start, timeFrame.end]
  );

  const {
    isLoading: isLoadingConnection,
    isError: errorConnectionInfo,
    isFetching: fetchingConnectionInfo,
  } = useMultipleQueries(
    MonitoringAPI.endpoints.getConnectionInfoByID,
    connectionArgs,
    {
      skip: labStateFilters.labFiltersFeatures.labConnectionFeature === false,
    }
  );
  const {
    isLoading: loadingBandwidthSensorData,
    isFetching: fetchingBandwidthSensorData,
    error: errorBandwidthSensorData,
  } = useMultipleQueries(
    MonitoringAPI.endpoints.getSensordataByDeviceID,
    labBandwidthFeatureArgs,
    {
      skip: labStateFilters.labFiltersFeatures.labBandwidthFeature === false,
    }
  );

  const {
    isLoading: loadingEvents,
    isFetching: fetchingEvents,
    error: errorEvents,
  } = useMultipleQueries(MonitoringAPI.endpoints.getEventsByID, eventsArgs);

  const {
    isLoading: loadingSensorData,
    isFetching: fetchingSensorData,
    error: errorSensorData,
  } = useMultipleQueries(
    MonitoringAPI.endpoints.getSensordataByDeviceID,
    sensorArgs
  );

  const loading =
    !readyToFetch ||
    loadingEvents ||
    loadingSensorData ||
    fetchingEvents ||
    fetchingSensorData ||
    isLoadingConnection ||
    fetchingConnectionInfo ||
    loadingDevicesControlUnits ||
    loadingBandwidthSensorData ||
    fetchingBandwidthSensorData;

  const error =
    errorEvents ||
    errorSensorData ||
    errorConnectionInfo ||
    errorBandwidthSensorData;

  const { sensorData, digitalData } = useAppSelector((state) =>
    getSensorInformation(state, {
      devicesControlUnits,
      digitalFilterObjectEvents,
      analogFilterObjectEvents,
      start: timeFrame.start.valueOf(),
      end: timeFrame.end.valueOf(),
      showLevelOption: showLevelView,
      preferredTemperatureUnit: preferredTemperatureUnit,
      labStateFilters: labStateFilters,
    })
  );

  const digitalChannels = useMemo(() => {
    return devicesIds.reduce(
      (acc, deviceId) => ({
        ...acc,
        ...digitalData[deviceId],
      }),
      {} as STTimeLineChartData
    );
  }, [devicesIds, digitalData]);

  const sensordataFiltered = useMemo(() => {
    if (loading) return [];
    return filterAnalogChannels(
      sensorData,
      analogFilterObjectEvents,
      cloudGeneratedFilterLogicalObject
    );
  }, [
    sensorData,
    analogFilterObjectEvents,
    cloudGeneratedFilterLogicalObject,
    loading,
  ]);

  const digitalChannelsFiltered = useMemo(() => {
    if (loading) return {} as STTimeLineChartData;
    return filterExtrapolatedDigitalChannelData(
      filterDigitalChannels(
        digitalChannels,
        digitalFilterObjectEvents,
        connectionDigitalFilterObject
      ),
      timeFrame.start.unix(),
      timeFrame.end.unix()
    );
  }, [
    digitalChannels,
    digitalFilterObjectEvents,
    connectionDigitalFilterObject,
    loading,
    timeFrame.end,
    timeFrame.start,
  ]);

  // Fetch
  useEffect(() => {
    if (
      !(loadingDevicesControlUnits || loadingCUsTranslate) &&
      readyToFetch !== true
    ) {
      setDigitalFilterObject(defaultFilterObjectEvents);
      setAnalogFilterObject(defaultFilterObjectChannels);
      setReadyToFetch(true);
    }
  }, [
    defaultFilterObjectChannels,
    defaultFilterObjectEvents,
    loadingCUsTranslate,
    loadingDevicesControlUnits,
    readyToFetch,
    setAnalogFilterObject,
    setDigitalFilterObject,
  ]);

  return {
    timeControls: { timeFrame, setTimeFrame },
    digitalControls: [
      digitalFilterObjectEvents,
      externalSetDigitalFilterObject,
    ] as typeof digitalControls,
    analogControls: [
      analogFilterObjectEvents,
      externalSetAnalogFilterObject,
    ] as typeof analogControls,
    labStateFilters,
    metrics: {
      sensordata: sensordataFiltered,
      digitalChannels: digitalChannelsFiltered,
    },
    loading: loading && devicesControlUnits.length > 0,
    error,
  };
};
